// CercleDigital InsiderGems, gestion électronique documentaire partagée de l'écosystème Cercle.
// Copyright 2026 Pierre-Thibault Fabre <pierre-thibault@cercle.digital>
// SPDX-License-Identifier: LicenseRef-Cercle-Interne
//
// Fichier : apps/web/src/lib/fondu.ts
// Rôle : ordonne les substitutions de contenu derrière un fondu.
// Référence : designsystem.md ; CIG-SPECIFICATION.md §5.5.

/**
 * Durée du fondu, en millisecondes. Elle doit rester égale à `--cig-duree-courte`
 * du jeu de jetons : une valeur plus courte laisserait voir la substitution,
 * une valeur plus longue retiendrait le contenu déjà disponible.
 */
export const DUREE_FONDU = 130;

/** Attente simple, pour aligner une substitution sur la fin d'un fondu. */
export function delai(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Substitue un contenu sans jamais le montrer en train de changer.
 *
 * Le fondu sortant et le chargement courent ensemble : la substitution
 * n'intervient qu'une fois les deux terminés, donc à l'écran vide. Changer le
 * contenu pendant le fondu produirait un chevauchement visible, l'ancien et le
 * nouveau se mélangeant à mi-opacité.
 *
 * L'appelant fournit deux gestes distincts : `charger` ne fait qu'obtenir les
 * données, `appliquer` ne fait que les poser. Les mêler ferait réapparaître le
 * défaut, puisque le chargement écrirait au fil de l'eau.
 */
export async function substituer<T>(
  masquer: (masque: boolean) => void,
  charger: () => Promise<T>,
  appliquer: (valeur: T) => void,
): Promise<void> {
  masquer(true);
  const attente = delai(DUREE_FONDU);
  try {
    const [valeur] = await Promise.all([charger(), attente]);
    appliquer(valeur);
  } finally {
    // Le contenu redevient visible même en cas d'échec : un panneau resté
    // transparent laisserait l'utilisateur devant un vide inexplicable.
    masquer(false);
  }
}
